import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Calendar,
  DollarSign,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ItineraryItem from './ItineraryItem';
import type {
  ItineraryDay as ItineraryDayType,
  ItineraryItem as ItineraryItemType,
} from '../types/itinerary';
import { calculateDayTotal } from '../lib/scheduler';

interface ItineraryDayProps {
  day: ItineraryDayType;
  onAddItem?: (dayId: string) => void;
  onEditItem?: (item: ItineraryItemType) => void;
  onDeleteItem?: (itemId: string) => void;
  onReorderItems?: (dayId: string, items: ItineraryItemType[]) => void;
}

export default function ItineraryDay({
  day,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onReorderItems,
}: ItineraryDayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [items, setItems] = useState(() => {
    // Safe sorting with date validation
    return [...day.items].sort((a, b) => {
      const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
      const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
      return dateA - dateB;
    });
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Day';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Day';
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  const dayTotal = calculateDayTotal(day);

  // Group items by time period
  const getTimePeriod = (item: ItineraryItemType) => {
    if (!item.startTime) return 'evening';
    const date = new Date(item.startTime);
    if (isNaN(date.getTime())) return 'evening';
    const hour = date.getHours();
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 18) return 'afternoon';
    return 'evening';
  };

  const morningItems = items.filter((item) => getTimePeriod(item) === 'morning');
  const afternoonItems = items.filter(
    (item) => getTimePeriod(item) === 'afternoon'
  );
  const eveningItems = items.filter((item) => getTimePeriod(item) === 'evening');

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Notify parent
      if (onReorderItems) {
        onReorderItems(day.id, newItems);
      }
    }
  };

  const TimeSection = ({
    title,
    sectionItems,
    gradient,
  }: {
    title: string;
    sectionItems: ItineraryItemType[];
    gradient: string;
  }) => (
    <div className="mb-4">
      <div
        className={`text-xs font-semibold uppercase tracking-wide px-3 py-1 ${gradient} rounded-lg mb-2 inline-block`}
      >
        {title}
      </div>
      <div className="space-y-2">
        {sectionItems.length === 0 ? (
          <p className="text-sm text-gray-400 italic px-3">
            No activities scheduled
          </p>
        ) : (
          sectionItems.map((item) => (
            <ItineraryItem
              key={item.id}
              item={item}
              onEdit={onEditItem}
              onDelete={onDeleteItem}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Day Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-primary-50 to-white hover:from-primary-100 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
            {day.dayNumber}
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900">{formatDate(day.date)}</h3>
            <p className="text-sm text-gray-500">
              {day.items.length} {day.items.length === 1 ? 'activity' : 'activities'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {dayTotal > 0 && (
            <div className="flex items-center gap-1 text-sm font-medium text-gray-600">
              <DollarSign className="h-4 w-4" />
              <span>{dayTotal.toLocaleString()}</span>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Day Content */}
      {isExpanded && (
        <div className="p-4">
          {/* Day Notes */}
          {day.notes && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">{day.notes}</p>
            </div>
          )}

          {/* Sortable Items */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {morningItems.length > 0 && (
                <TimeSection
                  title="Morning"
                  sectionItems={morningItems}
                  gradient="bg-orange-100 text-orange-700"
                />
              )}

              {afternoonItems.length > 0 && (
                <TimeSection
                  title="Afternoon"
                  sectionItems={afternoonItems}
                  gradient="bg-blue-100 text-blue-700"
                />
              )}

              {eveningItems.length > 0 && (
                <TimeSection
                  title="Evening"
                  sectionItems={eveningItems}
                  gradient="bg-indigo-100 text-indigo-700"
                />
              )}
            </SortableContext>
          </DndContext>

          {/* Add Item Button */}
          {onAddItem && (
            <button
              onClick={() => onAddItem(day.id)}
              className="w-full mt-4 py-3 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">Add Activity</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
